import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { InviteUserDialog } from "@/components/admin/InviteUserDialog";
import { ManualUserAddDialog } from "@/components/admin/ManualUserAddDialog";
import { Copy, Mail, CheckCircle, Clock, XCircle, RefreshCw, ArrowLeft, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Invite {
  id: string;
  email: string;
  access_level: string;
  status: string;
  custom_message: string | null;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  invite_token: string;
}

const AdminInvites = () => {
  const navigate = useNavigate();
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchInvites = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('user_invites')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching invites:', error);
      toast.error("Failed to load invites");
    } else {
      setInvites(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate("/auth");
      return;
    }

    const { data: roleData, error: roleError } = await supabase
      .from("user_roles")
      .select("*")
      .eq("user_id", session.user.id)
      .eq("role", "admin")
      .maybeSingle();

    if (roleError || !roleData) {
      toast.error("Access denied: Admin privileges required");
      navigate("/dashboard");
      return;
    }

    setIsAdmin(true);
    fetchInvites();
  };

  const copyInviteLink = (token: string) => {
    const baseUrl = window.location.origin;
    const inviteUrl = `${baseUrl}/sign-up?invite=${token}`;
    navigator.clipboard.writeText(inviteUrl);
    toast.success("Invite link copied to clipboard!");
  };

  const getStatusBadge = (status: string, expiresAt: string) => {
    if (status === 'accepted') {
      return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Accepted</Badge>;
    }
    if (status === 'revoked') {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Revoked</Badge>;
    }
    if (new Date(expiresAt) < new Date()) {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
    }
    return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  };

  const getAccessLevelBadge = (level: string) => {
    const colors = {
      full_access: "bg-blue-500",
      community_only: "bg-purple-500",
      dashboard_only: "bg-orange-500"
    };
    return (
      <Badge className={colors[level as keyof typeof colors]}>
        {level.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const resendInvite = async (inviteId: string, email: string) => {
    setActionLoading(true);
    try {
      // For now, we'll just update the expires_at date
      const newExpiresAt = new Date();
      newExpiresAt.setDate(newExpiresAt.getDate() + 7);

      const { error } = await supabase
        .from('user_invites')
        .update({ expires_at: newExpiresAt.toISOString() })
        .eq('id', inviteId);

      if (error) throw error;

      toast.success(`Invitation resent to ${email}`);
      fetchInvites();
    } catch (error: any) {
      console.error('Error resending invite:', error);
      toast.error("Failed to resend invitation");
    } finally {
      setActionLoading(false);
    }
  };

  const revokeInvite = async () => {
    if (!selectedInvite) return;
    
    setActionLoading(true);
    try {
      const { error } = await supabase
        .from('user_invites')
        .update({ status: 'revoked' })
        .eq('id', selectedInvite);

      if (error) throw error;

      toast.success("Invitation revoked successfully");
      fetchInvites();
      setRevokeDialogOpen(false);
      setSelectedInvite(null);
    } catch (error: any) {
      console.error('Error revoking invite:', error);
      toast.error("Failed to revoke invitation");
    } finally {
      setActionLoading(false);
    }
  };

  const isExpired = (expiresAt: string) => new Date(expiresAt) < new Date();
  const canResend = (invite: Invite) => 
    invite.status === 'pending' && !isExpired(invite.expires_at);
  const canRevoke = (invite: Invite) => 
    invite.status === 'pending' && !isExpired(invite.expires_at);

  if (loading || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="container mx-auto px-4 py-12 max-w-6xl">
        <Link to="/admin">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin Dashboard
          </Button>
        </Link>

        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">User Invitations</h1>
            <p className="text-muted-foreground">Manually invite users and manage access levels</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchInvites} size="icon" disabled={actionLoading}>
              <RefreshCw className={`w-4 h-4 ${actionLoading ? 'animate-spin' : ''}`} />
            </Button>
            <ManualUserAddDialog onSuccess={fetchInvites} />
            <InviteUserDialog />
          </div>
        </div>

      <div className="grid gap-4">
        {invites.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Mail className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium mb-2">No invitations yet</p>
              <p className="text-muted-foreground mb-4">Invite users via email or add them manually</p>
              <div className="flex gap-2">
                <ManualUserAddDialog onSuccess={fetchInvites} />
                <InviteUserDialog />
              </div>
            </CardContent>
          </Card>
        ) : (
          invites.map((invite) => (
            <Card key={invite.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{invite.email}</CardTitle>
                    <CardDescription className="flex gap-2 mt-2">
                      {getStatusBadge(invite.status, invite.expires_at)}
                      {getAccessLevelBadge(invite.access_level)}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyInviteLink(invite.invite_token)}
                      disabled={invite.status === 'accepted' || isExpired(invite.expires_at) || invite.status === 'revoked'}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </Button>
                    {canResend(invite) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => resendInvite(invite.id, invite.email)}
                        disabled={actionLoading}
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Resend
                      </Button>
                    )}
                    {canRevoke(invite) && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setSelectedInvite(invite.id);
                          setRevokeDialogOpen(true);
                        }}
                        disabled={actionLoading}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {invite.custom_message && (
                    <div>
                      <span className="font-medium">Message:</span>
                      <p className="text-muted-foreground mt-1">{invite.custom_message}</p>
                    </div>
                  )}
                  <div className="flex gap-4 text-muted-foreground">
                    <span>Created {formatDistanceToNow(new Date(invite.created_at), { addSuffix: true })}</span>
                    {invite.accepted_at ? (
                      <span>Accepted {formatDistanceToNow(new Date(invite.accepted_at), { addSuffix: true })}</span>
                    ) : (
                      <span>Expires {formatDistanceToNow(new Date(invite.expires_at), { addSuffix: true })}</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <AlertDialog open={revokeDialogOpen} onOpenChange={setRevokeDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Invitation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke this invitation? This action cannot be undone and the invite link will no longer work.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={revokeInvite} disabled={actionLoading}>
              {actionLoading ? "Revoking..." : "Revoke"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default AdminInvites;
